import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Search, Download, Send, Trash2, Eye, X, Plus, Check,
  CreditCard, Banknote, Landmark, Building2, Pencil, Save, AlertCircle, Link2, Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = d => d ? d.split('-').reverse().join('/') : '—';

const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const TYPE_BADGE = {
  acompte: "bg-blue-100 text-blue-700",
  solde: "bg-purple-100 text-purple-700",
};
const MODE_LABELS = {
  especes: { label: "Espèces", icon: Banknote, color: "text-green-600" },
  cheque: { label: "Chèque", icon: CreditCard, color: "text-orange-600" },
  virement: { label: "Virement", icon: Landmark, color: "text-blue-600" },
  cb: { label: "Carte bancaire", icon: Building2, color: "text-purple-600" },
  cb_stripe: { label: "CB Stripe", icon: CreditCard, color: "text-indigo-600" },
};

// ── Règlement Form ─────────────────────────────────────────────────────────────
function ReglementForm({ devisId, montantRestant, onSaved }) {
  const { authHeaders } = useAuth();
  const [form, setForm] = useState({
    montant: montantRestant > 0 ? montantRestant : "",
    date: new Date().toISOString().slice(0, 10),
    mode: "virement",
    reference: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (!form.montant || form.montant <= 0) { setError("Montant invalide"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/reglements`, { ...form, devis_id: devisId, montant: parseFloat(form.montant) }, { headers: authHeaders });
      onSaved();
      setForm(f => ({ ...f, montant: "", reference: "", notes: "" }));
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-green-50 border border-green-200 p-4 rounded space-y-3">
      <h4 className="font-semibold text-green-800 flex items-center gap-2 text-sm">
        <Plus size={14} /> Enregistrer un règlement
      </h4>
      {error && <p className="text-red-600 text-xs flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Montant (€) *</label>
          <input type="number" step="0.01" value={form.montant}
            onChange={e => setForm(f => ({ ...f, montant: e.target.value }))} required
            className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 rounded bg-white" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Date *</label>
          <input type="date" value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required
            className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 rounded bg-white" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Mode *</label>
          <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
            className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 rounded bg-white">
            {Object.entries(MODE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Référence</label>
          <input type="text" value={form.reference} placeholder="N° chèque, virement..."
            onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
            className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 rounded bg-white" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Notes</label>
        <input type="text" value={form.notes} placeholder="Remarques..."
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 rounded bg-white" />
      </div>
      <button type="submit" disabled={saving} data-testid="save-reglement-btn"
        className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm font-semibold hover:bg-green-800 disabled:opacity-60 transition-colors rounded">
        {saving ? <span className="animate-spin">⟳</span> : <Check size={14} />}
        {saving ? "Enregistrement..." : "Enregistrer le règlement"}
      </button>
    </form>
  );
}

// ── Facture Modal ──────────────────────────────────────────────────────────────
function FactureModal({ invoice, onClose, onUpdated }) {
  const { authHeaders } = useAuth();
  const [inv, setInv] = useState(invoice);
  const [editing, setEditing] = useState(false);
  const [editMontant, setEditMontant] = useState(invoice.montant);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingReg, setDeletingReg] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const refresh = useCallback(async () => {
    const res = await axios.get(`${API}/invoices/${inv.id}`, { headers: authHeaders });
    setInv(res.data);
    onUpdated();
  }, [inv.id, authHeaders, onUpdated]);

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/invoices/${inv.id}`, { montant: parseInt(editMontant) }, { headers: authHeaders });
      await refresh();
      setEditing(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur mise à jour");
    } finally { setSavingEdit(false); }
  };

  const deleteReglement = async (regId) => {
    await axios.delete(`${API}/reglements/${regId}`, { headers: authHeaders });
    setDeletingReg(null);
    await refresh();
  };

  const copyPaymentLink = () => {
    const link = `${window.location.origin}/paiement?invoice=${inv.id}&type=${inv.type}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const downloadPdf = async () => {
    const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { headers: authHeaders, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `facture-${inv.numero}.pdf`; a.click();
  };

  const sendEmail = async () => {
    await axios.post(`${API}/invoices/${inv.id}/send`, {}, { headers: authHeaders });
    await refresh();
    alert(`Facture ${inv.numero} envoyée !`);
  };

  const pct = inv.devis_montant > 0 ? Math.min(100, Math.round((inv.total_regle / inv.devis_montant) * 100)) : 0;
  const soldé = inv.reste_solder <= 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full shadow-2xl rounded overflow-hidden max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-[#1B2E4B]">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-white font-bold text-base">{inv.numero}</p>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${inv.type === "acompte" ? "bg-blue-200 text-blue-800" : "bg-purple-200 text-purple-800"}`}>
                {inv.type === "acompte" ? "Acompte" : "Solde"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyPaymentLink} title={linkCopied ? "Lien copié !" : "Copier lien de paiement"}
              data-testid="copy-payment-link"
              className={`p-2 transition-colors rounded ${linkCopied ? "text-green-400" : "text-slate-400 hover:text-yellow-400"}`}>
              {linkCopied ? <Check size={16} /> : <Link2 size={16} />}
            </button>
            <button onClick={downloadPdf} title="Télécharger PDF"
              className="p-2 text-slate-400 hover:text-white transition-colors rounded">
              <Download size={16} />
            </button>
            <button onClick={sendEmail} title="Envoyer par email"
              className="p-2 text-slate-400 hover:text-green-400 transition-colors rounded">
              <Send size={16} />
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* Invoice details card */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Client</p>
              <p className="font-semibold text-gray-900">{inv.client_name}</p>
              <p className="text-sm text-gray-500">{inv.client_email}</p>
              <p className="text-sm text-gray-500">{inv.date_mariage && `Mariage: ${fmt(inv.date_mariage)}`}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Prestation</p>
              <p className="font-semibold text-gray-900">{inv.vehicle_name}</p>
              <p className="text-sm text-gray-500">Réf. devis: {inv.devis_numero}</p>
              <p className="text-sm text-gray-500">Total prestation: {inv.devis_montant || inv.montant_total}€</p>
            </div>
          </div>

          {/* Montant facture — éditable */}
          <div className="border border-gray-200 rounded p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Montant facturé</p>
              {!editing && (
                <button onClick={() => { setEditMontant(inv.montant); setEditing(true); }}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  <Pencil size={11} /> Modifier
                </button>
              )}
            </div>
            {editing ? (
              <div className="flex items-center gap-3">
                <input type="number" value={editMontant} onChange={e => setEditMontant(e.target.value)}
                  autoFocus data-testid="edit-invoice-montant"
                  className="w-32 border border-blue-300 px-3 py-2 text-lg font-bold focus:outline-none rounded" />
                <span className="text-lg font-bold text-gray-400">€</span>
                <button onClick={saveEdit} disabled={savingEdit}
                  className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 disabled:opacity-60">
                  {savingEdit ? "..." : <><Check size={14} /> Valider</>}
                </button>
                <button onClick={() => setEditing(false)}
                  className="px-3 py-2 border border-gray-200 text-sm rounded hover:bg-gray-50">
                  Annuler
                </button>
              </div>
            ) : (
              <p className="text-3xl font-bold text-gray-900">{inv.montant}€</p>
            )}
          </div>

          {/* Payment status bar */}
          <div className="border border-gray-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Suivi des paiements (devis)</p>
              <span className={`text-xs font-bold px-2 py-1 rounded ${soldé ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                {soldé ? "✓ Soldé" : `Reste: ${inv.reste_solder?.toFixed(2)}€`}
              </span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Réglé: <span className="font-bold text-gray-900">{inv.total_regle?.toFixed(2)}€</span></span>
              <span className="text-gray-500">Total: <span className="font-bold">{inv.devis_montant || inv.montant_total}€</span></span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-2 rounded-full transition-all ${soldé ? "bg-green-500" : "bg-[#D4A843]"}`}
                style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1 text-right">{pct}% réglé</p>
          </div>

          {/* Règlements list */}
          {inv.reglements?.length > 0 && (
            <div className="border border-gray-200 rounded overflow-hidden">
              <p className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                Règlements enregistrés ({inv.reglements.length})
              </p>
              <div className="divide-y divide-gray-50">
                {inv.reglements.map(reg => {
                  const modeInfo = MODE_LABELS[reg.mode] || { label: reg.mode, color: "text-gray-600" };
                  const ModeIcon = modeInfo.icon;
                  return (
                    <div key={reg.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
                      {ModeIcon && <ModeIcon size={16} className={modeInfo.color} />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{reg.montant?.toFixed(2)}€</p>
                        <p className="text-xs text-gray-400">
                          {fmt(reg.date)} · {modeInfo.label}
                          {reg.reference && ` · ${reg.reference}`}
                          {reg.notes && ` · ${reg.notes}`}
                        </p>
                      </div>
                      {deletingReg === reg.id ? (
                        <div className="flex gap-1">
                          <button onClick={() => deleteReglement(reg.id)}
                            className="text-xs px-2 py-1 bg-red-500 text-white rounded font-semibold">Oui</button>
                          <button onClick={() => setDeletingReg(null)}
                            className="text-xs px-2 py-1 border border-gray-200 rounded">Non</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeletingReg(reg.id)} title="Supprimer"
                          className="text-gray-300 hover:text-red-400 transition-colors p-1 rounded">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add règlement form */}
          {!soldé && (
            <ReglementForm
              devisId={inv.devis_id}
              montantRestant={inv.reste_solder}
              onSaved={refresh}
            />
          )}
          {soldé && (
            <div className="bg-green-50 border border-green-200 p-3 rounded flex items-center gap-2 text-green-700 text-sm font-semibold">
              <Check size={16} /> Ce devis est entièrement soldé.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AdminFactures() {
  const { authHeaders } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/invoices`, { headers: authHeaders })
      .then(r => setInvoices(r.data)).finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const openModal = async (inv) => {
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}`, { headers: authHeaders });
      setSelected(res.data);
    } catch {
      setSelected(inv);
    }
  };

  const filtered = invoices.filter(inv => {
    const matchType = typeFilter === "all" || inv.type === typeFilter;
    const matchSearch = !search || [inv.client_name, inv.numero, inv.devis_numero, inv.vehicle_name]
      .some(f => f?.toLowerCase().includes(search.toLowerCase()));
    return matchType && matchSearch;
  });

  const downloadPdf = async (e, inv) => {
    e.stopPropagation();
    const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { headers: authHeaders, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `facture-${inv.numero}.pdf`; a.click();
  };

  const sendEmail = async (e, inv) => {
    e.stopPropagation();
    await axios.post(`${API}/invoices/${inv.id}/send`, {}, { headers: authHeaders });
    alert(`Facture ${inv.numero} envoyée à ${inv.client_email}`);
    setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, sent: true } : i));
  };

  const deleteInvoice = async (id) => {
    await axios.delete(`${API}/invoices/${id}`, { headers: authHeaders });
    setDeleting(null); fetchInvoices();
  };

  const totalMontant = filtered.reduce((s, i) => s + i.montant, 0);
  const totalAcompte = filtered.filter(i => i.type === "acompte").reduce((s, i) => s + i.montant, 0);
  const totalSolde = filtered.filter(i => i.type === "solde").reduce((s, i) => s + i.montant, 0);
  const envoyees = filtered.filter(i => i.sent).length;

  const FILTERS = [
    { k: "all", label: "Toutes" },
    { k: "acompte", label: "Acomptes" },
    { k: "solde", label: "Soldes" },
  ];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="factures-title">Factures</h1>
          <p className="text-gray-500 text-sm mt-1">Cliquez sur une facture pour l'ouvrir, éditer et gérer les règlements</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total facturé", value: `${totalMontant}€`, color: "text-gray-900" },
          { label: "Acomptes", value: `${totalAcompte}€`, color: "text-blue-600" },
          { label: "Soldes", value: `${totalSolde}€`, color: "text-purple-600" },
          { label: "Envoyées", value: `${envoyees}/${filtered.length}`, color: "text-green-600" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white p-5 shadow-sm rounded">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 shadow-sm mb-4 flex flex-col sm:flex-row gap-3 rounded">
        <div className="flex gap-2">
          {FILTERS.map(f => (
            <button key={f.k} onClick={() => setTypeFilter(f.k)} data-testid={`filter-type-${f.k}`}
              className={`px-4 py-2 text-sm font-semibold border-2 transition-all rounded ${typeFilter === f.k ? "border-[#1B2E4B] bg-[#1B2E4B] text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
              {f.label}
              <span className="ml-1.5 text-xs opacity-70">
                ({f.k === "all" ? invoices.length : invoices.filter(i => i.type === f.k).length})
              </span>
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par client, n° facture, n° devis..."
            data-testid="factures-search"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843] rounded" />
        </div>
      </div>

      {/* ── Mobile card view ───────────────────────────────────── */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <p className="text-center py-10 text-gray-400">Chargement...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-10 text-gray-400">Aucune facture trouvée</p>
        ) : filtered.map(inv => (
          <div key={inv.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" onClick={() => openModal(inv)}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-bold text-[#1B2E4B]">{inv.numero}</p>
              <div className="flex items-center gap-1.5">
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${TYPE_BADGE[inv.type]}`}>
                  {inv.type === "acompte" ? "Acompte" : "Solde"}
                </span>
                {inv.sent
                  ? <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">Envoyée ✓</span>
                  : <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">Non envoyée</span>}
              </div>
            </div>
            <p className="font-semibold text-gray-900">{inv.client_name}</p>
            <p className="text-xs text-gray-400 mb-1">{inv.client_email}</p>
            <p className="text-sm text-gray-600">{inv.vehicle_name}</p>
            <p className="text-sm text-gray-500 mb-3">{fmt(inv.date_mariage)} · Devis {inv.devis_numero}</p>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <p className="text-xl font-bold text-gray-900">{inv.montant}€</p>
              <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                <button onClick={e => downloadPdf(e, inv)} className="text-gray-400 hover:text-blue-600"><Download size={16} /></button>
                <button onClick={e => sendEmail(e, inv)} className="text-gray-400 hover:text-green-600"><Send size={16} /></button>
                <button onClick={e => { e.stopPropagation(); setDeleting(inv.id); }} className="text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-600">Total ({filtered.length} facture{filtered.length > 1 ? "s" : ""})</p>
            <p className="text-xl font-bold text-gray-900">{totalMontant}€</p>
          </div>
        )}
      </div>

      {/* ── Desktop table view ───────────────────────────────────── */}
      <div className="hidden md:block bg-white shadow-sm overflow-hidden rounded">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="factures-table">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold tracking-wider uppercase bg-gray-50">
                {["N° Facture","Type","Client","Réf. Devis","Véhicule","Date Mariage","Montant","Envoyée","Actions"].map(h => (
                  <th key={h} className="px-4 py-4 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">Chargement...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">Aucune facture trouvée</td></tr>
              ) : filtered.map(inv => (
                <tr key={inv.id}
                  className="border-b border-gray-50 hover:bg-blue-50 transition-colors cursor-pointer"
                  onClick={() => openModal(inv)}
                  data-testid={`facture-row-${inv.id}`}>
                  <td className="px-4 py-3 font-medium text-[#1B2E4B]">{inv.numero}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${TYPE_BADGE[inv.type]}`}>
                      {inv.type === "acompte" ? "Acompte" : "Solde"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{inv.client_name}</p>
                    <p className="text-xs text-gray-400">{inv.client_email}</p>
                  </td>
                  <td className="px-4 py-3 text-[#D4A843] font-medium">{inv.devis_numero}</td>
                  <td className="px-4 py-3 text-gray-600">{inv.vehicle_name}</td>
                  <td className="px-4 py-3 text-gray-600">{fmt(inv.date_mariage)}</td>
                  <td className="px-4 py-3 font-bold text-gray-900">{inv.montant}€</td>
                  <td className="px-4 py-3">
                    {inv.sent
                      ? <span className="text-green-600 text-xs font-semibold">✓ Envoyée</span>
                      : <span className="text-gray-400 text-xs">Non</span>}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openModal(inv)} data-testid={`open-facture-${inv.id}`}
                        title="Ouvrir" className="text-gray-400 hover:text-[#1B2E4B] transition-colors">
                        <Eye size={14} />
                      </button>
                      <button onClick={e => downloadPdf(e, inv)} data-testid={`dl-facture-${inv.id}`}
                        title="Télécharger PDF" className="text-gray-400 hover:text-blue-600 transition-colors">
                        <Download size={14} />
                      </button>
                      <button onClick={e => sendEmail(e, inv)} data-testid={`send-facture-${inv.id}`}
                        title="Envoyer par email" className="text-gray-400 hover:text-green-600 transition-colors">
                        <Send size={14} />
                      </button>
                      <button onClick={e => { e.stopPropagation(); setDeleting(inv.id); }}
                        data-testid={`delete-facture-${inv.id}`}
                        title="Supprimer" className="text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan={6} className="px-4 py-3 font-semibold text-gray-700">
                    TOTAL ({filtered.length} facture{filtered.length > 1 ? "s" : ""})
                  </td>
                  <td className="px-4 py-3 font-bold text-gray-900 text-base">{totalMontant}€</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 max-w-sm w-full shadow-2xl rounded">
            <h3 className="font-bold text-gray-900 mb-3">Supprimer cette facture ?</h3>
            <p className="text-gray-500 text-sm mb-6">Cette action est irréversible.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded">Annuler</button>
              <button onClick={() => deleteInvoice(deleting)} data-testid="confirm-delete-facture"
                className="px-5 py-2 bg-red-500 text-white text-sm font-semibold hover:bg-red-600 rounded">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Facture Modal */}
      {selected && (
        <FactureModal
          invoice={selected}
          onClose={() => setSelected(null)}
          onUpdated={fetchInvoices}
        />
      )}
    </div>
  );
}
